/**
 * Where a composed AGU app surface currently is.
 *
 * The standalone apps expressed their places as Angular routes; a surface is a
 * routerless BaseDashboard, so the route table becomes this triple. Two levels
 * are deliberately separated:
 *
 *   - `ModuleKey` selects WHICH module owns the screen. The surface switches on
 *     it and renders that module's root component.
 *   - `View` and `RecordID` are the module's OWN business. The surface never
 *     interprets them; it only carries them so a module's internal navigation
 *     survives a module switch and a Back().
 *
 * TYPING NOTE (deliberate exception). `docs/dev-best-practices.md` §2 says to
 * derive union types from their source and never widen to `string`. Here the
 * framework boundary genuinely is open: the whole point of the composition
 * contract is that a new module can be added and composed without editing the
 * framework, so a closed union of module keys would defeat it. Modules narrow
 * their own half — `AGUAppLocation<GrantFinderView>` where
 * `GrantFinderView = 'search' | 'detail'` — so every module-internal comparison
 * is still a compile-time-checked union. Only the surface, whose job is to be
 * indifferent, sees the widened form.
 */
export interface AGUAppLocation<TView extends string = string> {
    /** Key of the module that owns the screen. Matches AGUAppModuleDefinition.Key. */
    readonly ModuleKey: string;
    /** A place inside that module. Narrowed per module via TView. */
    readonly View: TView;
    /** Record the view is scoped to, when the view is parameterized. */
    readonly RecordID?: string;
    /**
     * Where the member opened this place from — the crumb's first ancestor, "Back to {origin}" (P/detail.html:888, 1072-1084).
     * One level only: a `From` inside `From` is dropped on encode (`WithOneLevelFrom`), so a chain of details never grows the URL.
     * Typed with the open `string` view: an origin usually belongs to a different module than the place itself.
     */
    readonly From?: AGUAppLocation;
    /** A search the place opens already running (`q=`), e.g. Career Explorer -> Jobs. Omitted when empty or whitespace; trimmed on decode. */
    readonly Query?: string;
}

/** The location with any origin nested inside its origin dropped; the same object back when there is nothing to drop. */
export function WithOneLevelFrom<T extends AGUAppLocation>(location: T): T {
    const from = location.From;
    if (!from || !from.From) return location;
    const { From: _nested, ...origin } = from;
    return { ...location, From: origin };
}

/**
 * True when two locations name the same place. An absent and an empty `RecordID` are the same
 * place: `BuildLocationSearch` writes neither into the URL.
 */
export function IsSameLocation(a: AGUAppLocation, b: AGUAppLocation): boolean {
    if (a.ModuleKey !== b.ModuleKey || a.View !== b.View || (a.RecordID ?? '') !== (b.RecordID ?? '')) return false;
    if (NormalizedQuery(a.Query) !== NormalizedQuery(b.Query)) return false;
    // The same record opened from two places is two places: each has its own "Back to" (and its own `f=`).
    if (!a.From || !b.From) return !a.From && !b.From;
    return IsSameLocation(a.From, b.From);
}

/** The query as the URL carries it: trimmed, and '' for absent or whitespace. */
function NormalizedQuery(query: string | undefined): string {
    return query?.trim() ?? '';
}

/** Query-string keys the surface mirrors its location into (deep links, back/forward). `f` carries the origin as its own query string. */
export const LOCATION_PARAM_KEYS = { Module: 'm', View: 'v', Record: 'id', From: 'f', Query: 'q' } as const;

export function EncodeLocationParams(location: AGUAppLocation): Record<string, string | null> {
    return {
        [LOCATION_PARAM_KEYS.Module]: location.ModuleKey,
        [LOCATION_PARAM_KEYS.View]: location.View,
        [LOCATION_PARAM_KEYS.Record]: location.RecordID ?? null, // null removes the param
        [LOCATION_PARAM_KEYS.Query]: NormalizedQuery(location.Query) || null,
        // The origin's own `m=&v=&id=`, without ITS origin (one level); URLSearchParams percent-encodes it as one value.
        [LOCATION_PARAM_KEYS.From]: location.From ? BuildLocationSearch({ ...location.From, From: undefined }) : null,
    };
}

export function DecodeLocationParams(params: Record<string, string>): AGUAppLocation | null {
    const place = DecodePlace(params);
    if (!place) return null;
    // One level: the origin is decoded without its own `f`. A malformed origin (no module) is dropped, never thrown.
    const fromSearch = params[LOCATION_PARAM_KEYS.From];
    const from = fromSearch ? DecodePlace(SearchToRecord(fromSearch)) : null;
    return from ? { ...place, From: from } : place;
}

/** Module, view and record from decoded params — the location without an origin. Null without a module. */
function DecodePlace(params: Record<string, string>): AGUAppLocation | null {
    const moduleKey = params[LOCATION_PARAM_KEYS.Module]?.trim();
    if (!moduleKey) return null;
    const view = params[LOCATION_PARAM_KEYS.View]?.trim() || '';
    const record = params[LOCATION_PARAM_KEYS.Record]?.trim();
    const query = params[LOCATION_PARAM_KEYS.Query]?.trim();
    return { ModuleKey: moduleKey, View: view, ...(record ? { RecordID: record } : {}), ...(query ? { Query: query } : {}) };
}

/** The location keys of a raw query string. URLSearchParams never throws: an invalid escape such as `%ZZ` stays literal text. */
function SearchToRecord(search: string): Record<string, string> {
    const params = new URLSearchParams(search);
    const record: Record<string, string> = {};
    for (const key of Object.values(LOCATION_PARAM_KEYS)) {
        const value = params.get(key);
        if (value !== null) record[key] = value;
    }
    return record;
}

/**
 * Serialize a location into a URL query string (no leading `?`), for writing
 * straight through the History API (`Location.go` / `Location.replaceState`).
 *
 * WHY THIS EXISTS (and why the surface no longer goes through
 * `BaseResourceComponent.UpdateQueryParams()` / `NavigationService` for this).
 * `@memberjunction/ng-explorer-core@5.51.0`'s `ShellComponent.buildResourceUrl()`
 * builds the visible tab URL from tab `Configuration`, and it has an `appendQP()`
 * call on every branch EXCEPT the `isAppDefault && appName` one (dist/lib/shell/
 * shell.component.js:1677-1680) — the exact shape of a composite app's
 * (Research Accelerator's) default-dashboard tab, which has no `navItemName`.
 * So even though `UpdateQueryParams()` correctly writes params into the tab's
 * `Configuration.queryParams`, `syncUrlWithWorkspace()` (shell.component.js:1178)
 * recomputes the URL from that same broken branch, gets back the bare
 * `/app/research-accelerator` every time, and — because the comparison is
 * `currentUrl !== newUrl` — either never writes the params in the first place,
 * or (once the surface starts writing directly, see below) actively strips them
 * back off via a real `router.navigateByUrl()`. Bypassing `Router` entirely via
 * Angular's `Location.go()`/`replaceState()` (see `ra-explorer-host.adapter.ts`)
 * sidesteps that recomputation: those calls don't feed the POP-only stream
 * `Router` listens on (`@angular/common`'s `Location._subject`, only fed by
 * `onPopState`), so `Router.url` never reflects the write and
 * `syncUrlWithWorkspace`'s comparison never fires an undo — while a genuine
 * browser Back/Forward still round-trips correctly through the framework's own
 * popstate → `NavigationEnd` → `syncWorkspaceWithUrl` → `OnQueryParamsChanged`
 * pipeline, since that pipeline matches on path only (query params are stripped
 * before matching, `findTabForUrl`'s `appOnlyMatch` branch).
 */
export function BuildLocationSearch(location: AGUAppLocation): string {
    const params = EncodeLocationParams(location);
    const search = new URLSearchParams();
    for (const key of Object.values(LOCATION_PARAM_KEYS)) {
        const value = params[key];
        if (value !== null) search.set(key, value);
    }
    return search.toString();
}

/**
 * Decode a location straight from a raw `location.search` string (leading `?`
 * optional) instead of from `BaseResourceComponent.GetQueryParams()`.
 *
 * WHY THIS EXISTS. `GetQueryParams()` reads
 * `this.Data?.Configuration?.['queryParams']`, but
 * `DashboardResource.loadCodeBasedDashboard()` (ng-explorer-core's tab wrapper
 * for `resourceType: 'Dashboards'` + `Type: 'Code'`) never forwards its own
 * `Data`/`ParentTabId` down to the code-based dashboard instance it creates —
 * it only assigns `instance.Config` (the `{dashboard, userState}` pair, a
 * different property). So for any code-based `BaseDashboard` hosted this way
 * (this surface included), `Data` stays the default empty `ResourceData()` for
 * the component's whole lifetime, and `GetQueryParams()` always returns `{}`.
 * Reading the deep link straight from the document's own URL is unaffected by
 * that gap.
 */
export function DecodeLocationParamsFromSearch(search: string): AGUAppLocation | null {
    return DecodeLocationParams(SearchToRecord(search));
}
